/* eslint-disable no-console */
// ---------------------------------------------------------------------------
// Pipeline de procesamiento de video del Reporte Fotográfico (solo subidas nuevas).
//
// Dos operaciones, ambas 100% en el navegador y con import DINÁMICO de las libs
// pesadas (mp4box / ffmpeg.wasm) para que NO entren al bundle principal — recién se
// cargan al procesar un video:
//
//   stripAudio(file)   → Blob mp4 SIN pista de audio (remux, sin recodificar).
//   generarPoster(file)→ Blob jpeg con un frame (~1s) del video.
//
// stripAudio es OBLIGATORIO y fail-closed (regla 10.177): si no puede garantizar un
// video sin audio, LANZA — el llamador aborta la subida (nunca sube con audio en
// silencio). generarPoster es best-effort: si falla, el llamador sube con poster_url
// null y el render cae al placeholder.
//
// Elección de libs: primario mp4box.js (~1MB, sin SharedArrayBuffer → sin headers
// COOP/COEP que romperían Google Maps); fallback ffmpeg.wasm 0.11 single-thread
// (`-c:v copy -an`, tampoco necesita SharedArrayBuffer). El happy-path de mp4box no
// está verificado en navegador: por eso su salida se VALIDA (sin-audio + reproducible)
// y ante cualquier duda se cae al fallback.
// ---------------------------------------------------------------------------

// --- carga perezosa de las libs (cacheada) --------------------------------

let _MP4Box;
async function loadMp4box() {
  if (!_MP4Box) {
    const mod = await import("mp4box");
    _MP4Box = mod.default || mod;
  }
  return _MP4Box;
}

let _ffmpeg;
async function getFfmpeg() {
  if (!_ffmpeg) {
    const { createFFmpeg } = await import("@ffmpeg/ffmpeg");
    _ffmpeg = createFFmpeg({ log: false });
  }
  if (!_ffmpeg.isLoaded()) await _ffmpeg.load();
  return _ffmpeg;
}

// --- validadores de la salida ---------------------------------------------

// ¿El blob carga como video reproducible? (metadata OK, ancho y duración > 0). Prueba
// real de que el remux no quedó corrupto. Archivo local → sin CORS.
function esReproducible(blob) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const v = document.createElement("video");
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      URL.revokeObjectURL(url);
      resolve(ok);
    };
    v.muted = true;
    v.preload = "metadata";
    v.onloadedmetadata = () => finish(v.videoWidth > 0 && Number.isFinite(v.duration) && v.duration > 0);
    v.onerror = () => finish(false);
    setTimeout(() => finish(false), 8000);
    v.src = url;
  });
}

// ¿El blob NO tiene pista de audio y SÍ tiene video? (re-parse con mp4box). Si no se
// puede confirmar (parse falla), devuelve false → no damos por bueno el remux.
async function sinPistaAudio(blob) {
  try {
    const MP4Box = await loadMp4box();
    const ab = await blob.arrayBuffer();
    return await new Promise((resolve) => {
      const f = MP4Box.createFile();
      f.onError = () => resolve(false);
      f.onReady = (info) => resolve((info.audioTracks || []).length === 0 && (info.videoTracks || []).length > 0);
      ab.fileStart = 0;
      f.appendBuffer(ab);
      f.flush();
    });
  } catch {
    return false;
  }
}

// --- strip de audio: mp4box (primario) ------------------------------------

// Serializa el DecoderConfigRecord (avcC/hvcC) que addTrack espera: es el payload del
// box, sin el header de 8 bytes (size+type) que agrega box.write.
function serializarConfig(MP4Box, box) {
  const ds = new MP4Box.DataStream(undefined, 0, MP4Box.DataStream.BIG_ENDIAN);
  box.write(ds);
  return ds.buffer.slice(8);
}

// Remux copiando SOLO la pista de video a un mp4 nuevo (dropea el audio). Sin recodificar.
async function stripAudioMp4box(file) {
  const MP4Box = await loadMp4box();
  const ab = await file.arrayBuffer();
  return await new Promise((resolve, reject) => {
    const inFile = MP4Box.createFile();
    const outFile = MP4Box.createFile();
    let outId = null;
    let got = 0;
    let expected = Infinity;

    inFile.onError = (e) => reject(new Error("mp4box parse: " + e));
    inFile.onReady = (info) => {
      try {
        const vt = (info.videoTracks || [])[0];
        if (!vt) return reject(new Error("mp4box: sin pista de video"));
        expected = vt.nb_samples;
        const trak = inFile.getTrackById(vt.id);
        const entry = trak.mdia.minf.stbl.stsd.entries[0];
        const opts = {
          timescale: vt.timescale,
          width: (vt.video && vt.video.width) || vt.track_width,
          height: (vt.video && vt.video.height) || vt.track_height,
          nb_samples: vt.nb_samples,
          type: entry.type,
        };
        if (entry.avcC) opts.avcDecoderConfigRecord = serializarConfig(MP4Box, entry.avcC);
        else if (entry.hvcC) opts.hevcDecoderConfigRecord = serializarConfig(MP4Box, entry.hvcC);
        outId = outFile.addTrack(opts);
        inFile.setExtractionOptions(vt.id, null, { nbSamples: Infinity });
        inFile.start();
      } catch (err) {
        reject(err);
      }
    };
    inFile.onSamples = (id, user, samples) => {
      try {
        for (const s of samples) {
          outFile.addSample(outId, s.data, { duration: s.duration, cts: s.cts, dts: s.dts, is_sync: s.is_sync });
        }
        got += samples.length;
        if (got >= expected) {
          const buf = outFile.getBuffer();
          resolve(new Blob([buf], { type: "video/mp4" }));
        }
      } catch (err) {
        reject(err);
      }
    };

    ab.fileStart = 0;
    inFile.appendBuffer(ab);
    inFile.flush();
  });
}

// --- strip de audio: ffmpeg.wasm 0.11 (fallback) --------------------------

// `-c:v copy -an`: copia el stream de video (sin recodificar) y dropea el audio.
async function stripAudioFfmpeg(file) {
  const ffmpeg = await getFfmpeg();
  const inName = "in.mp4";
  const outName = "out.mp4";
  ffmpeg.FS("writeFile", inName, new Uint8Array(await file.arrayBuffer()));
  try {
    await ffmpeg.run("-i", inName, "-c:v", "copy", "-an", outName);
    const data = ffmpeg.FS("readFile", outName);
    return new Blob([data.buffer], { type: "video/mp4" });
  } finally {
    try {
      ffmpeg.FS("unlink", inName);
    } catch {
      /* archivo ya no existe en el FS virtual */
    }
    try {
      ffmpeg.FS("unlink", outName);
    } catch {
      /* idem */
    }
  }
}

// --- API pública ----------------------------------------------------------

/**
 * Devuelve un Blob mp4 del `file` SIN pista de audio. Primario mp4box (validado),
 * fallback ffmpeg.wasm 0.11. Si ninguno logra un video sin-audio válido, LANZA
 * (fail-closed, regla 10.177): el llamador debe abortar la subida.
 */
export async function stripAudio(file) {
  // 1) mp4box (liviano). Validar sin-audio + reproducible; ante cualquier falla, fallback.
  try {
    const blob = await stripAudioMp4box(file);
    if ((await sinPistaAudio(blob)) && (await esReproducible(blob))) return blob;
    console.warn("stripAudio: salida de mp4box no validó, usando fallback ffmpeg");
  } catch (e) {
    console.warn("stripAudio: mp4box falló, usando fallback ffmpeg:", e?.message || e);
  }
  // 2) ffmpeg.wasm 0.11. `-an` garantiza sin-audio; validamos que sea reproducible.
  try {
    const blob = await stripAudioFfmpeg(file);
    if (await esReproducible(blob)) return blob;
    throw new Error("salida de ffmpeg no reproducible");
  } catch (e) {
    console.error("stripAudio: ffmpeg falló:", e?.message || e);
  }
  // 3) Fail-closed: nunca subir con audio en silencio.
  throw new Error("No se pudo quitar el audio del video. La subida se abortó (no se sube con audio).");
}

/**
 * Genera un Blob JPEG con un frame (~1s, o 0 si dura menos) del `file`. Best-effort:
 * lanza si no puede — el llamador debe capturar y seguir con poster_url null.
 */
export async function generarPoster(file) {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  try {
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = url;

    await new Promise((res, rej) => {
      video.onloadedmetadata = () => res();
      video.onerror = () => rej(new Error("no se pudo cargar el video para el póster"));
      setTimeout(() => rej(new Error("timeout cargando metadata")), 8000);
    });

    const target = Number.isFinite(video.duration) && video.duration > 1 ? 1 : 0;
    await new Promise((res, rej) => {
      video.onseeked = () => res();
      video.onerror = () => rej(new Error("seek del póster falló"));
      setTimeout(() => rej(new Error("timeout en seek")), 8000);
      video.currentTime = target;
    });

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 360;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

    return await new Promise((res, rej) =>
      canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob devolvió null"))), "image/jpeg", 0.8)
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}
